import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Connection, ConnectionFilters, HostingType, ProvisioningStatus, TRANSFER_METHODS, TRANSFER_METHOD_LABELS, TransferMethod } from '../../types/connection';
import { ResourceStatusBadge } from '../shared/StatusBadge';
import { Button } from '../shared/Button';
import { useConnectionData } from '../../hooks/useConnectionData';
import { usePartnerData } from '../../hooks/usePartnerData';
import ConnectionDetails from './ConnectionDetails';
import CreateConnection from '../CreateConnection';
import { RoleGuard } from '../RoleGuard';

interface ConnectionListProps {
  onConnectionSelect?: (connection: Connection) => void;
}

function ConnectionList({ onConnectionSelect }: ConnectionListProps) {
  const { connectionId: urlConnectionId } = useParams<{ connectionId?: string }>();
  const navigate = useNavigate();

  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState<ConnectionFilters>({
    status: [],
    hostingType: [],
    transferMethod: [],
    partners: []
  });

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting
  const [sortField, setSortField] = useState<keyof Connection>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Get data from individual hooks
  const { connections, loading: connectionsLoading, error: connectionsError, refreshConnections } = useConnectionData();
  const { partners } = usePartnerData();



  // Filter, search, and sort data with memoization
  const filteredAndSortedData = useMemo(() => {
    let result = [...connections];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(search) ||
        c.partnerName.toLowerCase().includes(search) ||
        c.hostingType.toLowerCase().includes(search) ||
        c.transferMethod.toLowerCase().includes(search) ||
        c.status.toLowerCase().includes(search)
      );
    }

    // Apply filters
    if (filters.partners.length > 0) {
      result = result.filter(c => filters.partners.includes(c.partnerId));
    }
    if (filters.hostingType.length > 0) {
      result = result.filter(c => filters.hostingType.includes(c.hostingType));
    }
    if (filters.status.length > 0) {
      result = result.filter(c => filters.status.includes(c.provisioningStatus || 'active'));
    }
    if (filters.transferMethod.length > 0) {
      result = result.filter(c => filters.transferMethod.includes(c.transferMethod));
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle nested taskCounts
      if (sortField === 'taskCounts' as keyof Connection) {
        aVal = a.taskCounts?.total || 0;
        bVal = b.taskCounts?.total || 0;
      }

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Number comparison
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return result;
  }, [connections, searchTerm, filters, sortField, sortOrder]);

  // THUB-298: derived from the route, not stored in state.
  //
  // Same duplicated-state shape PartnerList had: `selectedConnection` mirrored what
  // /connections/:connectionId already tells us, and keeping the copy in sync needed two effects
  // (one re-syncing on every `connections` change, one seeding from the URL) plus an
  // exhaustive-deps suppression. Deriving makes a connection refresh flow into the open modal and
  // a deleted connection close it, with no copy to fall out of step.
  const selectedConnection: Connection | null = urlConnectionId
    ? connections.find(c => c.connectionId === urlConnectionId) ?? null
    : null;

  // Connection selection handler
  const handleConnectionSelect = useCallback((connection: Connection) => {
    navigate(`/connections/${connection.connectionId}`);
    onConnectionSelect?.(connection);
  }, [onConnectionSelect, navigate]);

  // Handle update callback - refresh connections list
  const handleConnectionUpdate = useCallback(async () => {
    refreshConnections();
  }, [refreshConnections]);

  // Sort handler
  const handleSort = useCallback((field: keyof Connection) => {
    setSortField(prevField => {
      if (prevField === field) {
        setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortOrder('asc');
      }
      return field;
    });
  }, []);

  // T053: Helper to get provisioning status from connection
  const getProvisioningStatus = useCallback((connection: Connection): ProvisioningStatus => {
    return connection.provisioningStatus || 'active';
  }, []);


  // Sort icon function
  const getSortIcon = useCallback((field: keyof Connection) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  }, [sortField, sortOrder]);

  // Clear filters handler
  const clearFilters = useCallback(() => {
    setFilters({
      status: [],
      hostingType: [],
      transferMethod: [],
      partners: []
    });
    setSearchTerm('');
  }, []);

  // Active filters check
  const hasActiveFilters = useMemo(() => {
    return searchTerm ||
      filters.partners.length > 0 ||
      filters.hostingType.length > 0 ||
      filters.status.length > 0 ||
      filters.transferMethod.length > 0;
  }, [searchTerm, filters]);

  // Connection statistics
  const connectionStats = useMemo(() => ({
    total: connections.length,
    ready: connections.filter(c => (c.provisioningStatus || 'active') === 'active').length,
    totalTasks: connections.reduce((sum, c) => sum + (c.taskCounts?.total || 0), 0)
  }), [connections]);

  if (connectionsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-neutral-500 dark:text-slate-400">Loading connections...</div>
      </div>
    );
  }

  if (connectionsError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <h3 className="text-lg font-semibold text-danger-600 dark:text-danger-400">Error Loading Connections</h3>
        <p className="text-neutral-600 dark:text-slate-400">{connectionsError}</p>
        <Button variant="primary" onClick={refreshConnections}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white">
            Connections
          </h2>
          <p className="text-sm text-neutral-500 dark:text-slate-400 mt-0.5">
            Manage connections and monitor status
          </p>
        </div>
        <RoleGuard minimumRole="admin">
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create Connection
          </Button>
        </RoleGuard>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search connections..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            className="
              w-full px-4 py-2.5
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              rounded-lg text-sm
              text-neutral-900 dark:text-slate-100
              placeholder:text-neutral-400 dark:placeholder:text-slate-500
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200
            "
          />
          {searchTerm && (
            <button
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                w-5 h-5 flex items-center justify-center
                text-neutral-400 hover:text-neutral-600
                dark:text-slate-500 dark:hover:text-slate-300
              "
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={filters.partners[0] || ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters(prev => ({
                ...prev,
                partners: value ? [value] : []
              }));
            }}
            className="
              px-3 py-2.5 rounded-lg text-sm
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-slate-300
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Partners</option>
            {partners.map(partner => (
              <option key={partner.partnerId} value={partner.partnerId}>
                {partner.name}
              </option>
            ))}
          </select>

          {/* THUB-273: transfer METHOD filter.
              This control was labelled "Types", offered the single value "sftp", and wrote
              it into `filters.hostingType` — a field that is only ever 'hosted' | 'remote'.
              The predicate `filters.hostingType.includes(c.hostingType)` could therefore
              never match, so selecting the only option always returned ZERO rows. The
              `as any` cast is what let it compile; `HostingType` would have rejected 'sftp'.

              It now drives `filters.transferMethod`, whose filter logic already existed and
              was simply never fed, and the options come from TRANSFER_METHODS so a newly
              supported method cannot silently go missing again (azure_blob and api were
              both absent, making half the connections in DEV unfilterable). */}
          <select
            aria-label="Filter by transfer method"
            value={filters.transferMethod[0] || ''}
            onChange={(e) => {
              const value = e.target.value as TransferMethod | '';
              setFilters(prev => ({
                ...prev,
                transferMethod: value ? [value] : []
              }));
            }}
            className="
              px-3 py-2.5 rounded-lg text-sm
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-slate-300
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Methods</option>
            {TRANSFER_METHODS.map(method => (
              <option key={method} value={method}>{TRANSFER_METHOD_LABELS[method]}</option>
            ))}
          </select>

          {/* THUB-273: the hosting-type filter that "All Types" was mis-wired to.
              The table already sorts by this column ("Type" -> hosted/remote) but offered
              no way to filter it. */}
          <select
            aria-label="Filter by hosting type"
            value={filters.hostingType[0] || ''}
            onChange={(e) => {
              const value = e.target.value as HostingType | '';
              setFilters(prev => ({
                ...prev,
                hostingType: value ? [value] : []
              }));
            }}
            className="
              px-3 py-2.5 rounded-lg text-sm
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-slate-300
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Hosting</option>
            <option value="hosted">Hosted</option>
            <option value="remote">Remote</option>
          </select>

          <select
            value={filters.status[0] || ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters(prev => ({
                ...prev,
                status: value ? [value as any] : []
              }));
            }}
            className="
              px-3 py-2.5 rounded-lg text-sm
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-slate-300
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="provisioning">Provisioning</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="deleting">Deleting</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="
                px-3 py-2.5 rounded-lg text-sm
                text-neutral-600 dark:text-slate-400
                hover:text-neutral-900 dark:hover:text-slate-100
                hover:bg-neutral-100 dark:hover:bg-slate-700
                transition-colors duration-150
              "
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Filter Results */}
      {hasActiveFilters && (
        <div className="text-sm text-neutral-600 dark:text-slate-400">
          Showing {filteredAndSortedData.length} of {connectionStats.total} connections
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 dark:bg-slate-800">
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('name')}
                >
                  Connection Name {getSortIcon('name')}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('partnerName')}
                >
                  Partner {getSortIcon('partnerName')}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('hostingType')}
                >
                  Type {getSortIcon('hostingType')}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('transferMethod')}
                >
                  Method {getSortIcon('transferMethod')}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('taskCounts' as keyof Connection)}
                >
                  Tasks {getSortIcon('taskCounts' as keyof Connection)}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('status')}
                >
                  Status {getSortIcon('status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-neutral-500 dark:text-slate-400 italic">
                    {hasActiveFilters ? 'No connections match your filters' : 'No connections found'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedData.map(connection => (
                  <tr
                    key={connection.connectionId}
                    onClick={() => handleConnectionSelect(connection)}
                    className="
                      border-b border-neutral-100 dark:border-slate-700/50
                      hover:bg-primary-50 dark:hover:bg-primary-900/20
                      cursor-pointer transition-colors duration-150
                    "
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-neutral-900 dark:text-slate-100">{connection.name}</span>
                        {(connection.pgpPublicKeyConfigured || connection.pgpPrivateKeyConfigured) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300" title="PGP encryption configured">
                            PGP
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-neutral-700 dark:text-slate-300">
                      {connection.partnerName}
                    </td>
                    <td className="px-4 py-3.5 text-neutral-700 dark:text-slate-300">
                      {connection.hostingType}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="px-2 py-0.5 rounded text-xs font-medium uppercase bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                        {connection.transferMethod}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-neutral-900 dark:text-slate-100">
                        {(connection.taskCounts?.total || 0) === 0
                          ? '0'
                          : (connection.taskCounts?.enabled === connection.taskCounts?.total)
                            ? `${connection.taskCounts?.total}`
                            : `${connection.taskCounts?.total} (${connection.taskCounts?.enabled || 0} enabled)`
                        }
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <ResourceStatusBadge
                          status={getProvisioningStatus(connection)}
                          size="sm"
                        />
                        {(getProvisioningStatus(connection) === 'active') && (
                          connection.connectionTestResults && connection.connectionTestResults.success ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300">
                              Passed
                            </span>
                          ) : connection.connectionTestResults && !connection.connectionTestResults.success ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300">
                              Failed
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 dark:bg-slate-700 text-neutral-500 dark:text-slate-400">
                              Not Tested
                            </span>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredAndSortedData.length > 0 && (
          <div className="px-4 py-3 bg-neutral-50 dark:bg-slate-800 border-t border-neutral-200 dark:border-slate-700">
            <span className="text-sm text-neutral-500 dark:text-slate-400">
              {hasActiveFilters
                ? `Showing ${filteredAndSortedData.length} of ${connectionStats.total} connections`
                : `${filteredAndSortedData.length} connections`
              }
            </span>
          </div>
        )}
      </div>

      {/* Connection Details Modal */}
      {selectedConnection && (
        <ConnectionDetails
          connection={selectedConnection}
          partners={partners}
          onClose={() => {
            navigate('/connections');
            refreshConnections();
          }}
          onUpdate={handleConnectionUpdate}
        />
      )}

      {/* Create Connection Modal */}
      {showCreateModal && (
        <CreateConnection
          partners={partners}
          onClose={() => setShowCreateModal(false)}
          onCreate={() => {
            setShowCreateModal(false);
            refreshConnections();
          }}
        />
      )}
    </div>
  );
}

export default ConnectionList;
